<template>
  <b-modal
    :id="id"
    hide-header
    centered
    size="xl"
    @show="resetParam"
    scrollable
    dialog-class="pc-adjust"
    no-close-on-backdrop
  >
    <template #default="{ close }">
      <div v-if="isMobile">
        <b-row class="d-flex" no-gutters>
          <div class="category-edit-title">カテゴリー設定</div>
        </b-row>
        <category-list
          :categories="categorySource"
          :isMobile="isMobile"
          :attachedCategories="attachedCategories"
          :me="me"
          :domainId="domainId"
          v-on:selected="selected"
        />
        <b-row class="pt-3" no-gutters>
          <b-form-textarea
            rows="3"
            class="category-textarea"
            placeholder="追加するカテゴリー(改行区切りで複数入力可)"
            v-model="categoriesStr"
          />
        </b-row>
        <b-row class="pt-2" no-gutters>
          <custom-button label="カテゴリー追加" v-on:confirm="addCategories" />
        </b-row>
      </div>
      <div v-else>
        <b-row class="d-flex m-1 justify-content-end">
          <div @click="showCancelAlert(close)"><b-icon-x class="x-btn"></b-icon-x></div>
        </b-row>
        <b-row class="d-flex">
          <b-col class="col-8 col-lg-7 col-xl-6">
            <b-row class="d-flex" no-gutters>
              <div class="category-setting-label">カテゴリー設定</div>
            </b-row>
            <category-list
              :categories="categorySource"
              :isMobile="isMobile"
              :attachedCategories="attachedCategories"
              :me="me"
              :domainId="domainId"
              v-on:selected="selected"
            />
          </b-col>
          <b-col class="col-4 col-lg-5 col-xl-6">
            <b-row class="pt-4" no-gutters>
              <b-form-textarea
                rows="5"
                class="category-textarea"
                placeholder="追加するカテゴリー(改行区切りで複数入力可)"
                v-model="categoriesStr"
              />
            </b-row>
            <b-row class="pt-2" no-gutters>
              <custom-button label="カテゴリー追加" v-on:confirm="addCategories" />
            </b-row>
          </b-col>
        </b-row>
      </div>

      <category-title-edit
        id="category-title-edit"
        :category="editCategory"
        :categories="categorySource"
        :domainId="domainId"
        :me="me"
      />

      <b-modal id="category-add-alert" hide-header hide-footer centered>
        <template #default="{ ok }">
          <b-row v-if="duplicatedInput.length" no-gutters>
            <div>同じカテゴリーが入力されています</div>
            <b-form-textarea
              rows="2"
              class="category-textarea"
              v-model="duplicatedInput"
              readonly
            />
          </b-row>
          <b-row class="pt-2" v-if="duplicated.length" no-gutters>
            <div>既に同じものが登録されています</div>
            <b-form-textarea rows="2" class="category-textarea" v-model="duplicated" readonly />
          </b-row>
          <b-row class="pt-2" v-if="overed" no-gutters>
            <div>{{ validTitleMessage }}</div>
            <b-form-textarea rows="2" class="category-textarea" v-model="overed" readonly />
          </b-row>
          <b-row class="pt-2 flex-row-reverse" no-gutters>
            <confirm class="col-3" label="OK" v-on:confirm="ok" />
          </b-row>
        </template>
      </b-modal>
    </template>

    <template #modal-footer="{ close }">
      <b-row class="w-100 m-0 flex-nowrap justify-content-end">
        <cancel
          class="category-edit-footer-btn"
          label="キャンセル"
          v-on:cancel="showCancelAlert(close)"
        />
        <confirm class="category-edit-footer-btn ml-3" v-on:confirm="onEditComplete" label="保存" />
      </b-row>
    </template>
  </b-modal>
</template>
<style scoped>
.category-setting-label {
  padding-bottom: 8px;
  font-weight: bold;
  font-size: 14px;
}

.category-textarea {
  font-size: 14px;
  background-color: #f3f3f3;
}

/* ボタン側の固定幅(167px)を col-* の max-width が下回るとラベルが折り返し、
   固定高(42px)から溢れる。col-* を使わず flex の縮小に任せる。
   flex アイテムの min-width:auto がラベル幅(min-content)を下限にするため、
   nowrap だけで折り返しも溢れも防げる */
.category-edit-footer-btn {
  white-space: nowrap;
}

::placeholder {
  color: #bbbbbb;
}
</style>
<style scss>
/* PC用調整 */
@media (min-width: 768px) and (max-width: 1200px) {
  .modal-dialog.pc-adjust {
    max-width: 80%;
  }
}
</style>
<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  ref,
  type PropType,
} from "vue";
import type Vue from "vue";
import { Category, User } from "../model";
import CategoryList from "./CategoryList.vue";
import CustomButton from "./button/CustomButon.vue";
import Confirm from "./button/Confirm.vue";
import Cancel from "./button/Cancel.vue";
import CategoryTitleEdit from "./CategoryTitleEdit.vue";
import { useTextProcess } from "@/composables/useTextProcess";
import { useEditDiscard } from "@/composables/useEditDiscard";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";

// カテゴリータイトルの最大文字数
const CATEGORY_TITLE_MAX_LENGTH = 20;

export default defineComponent({
  name: "category-edit",
  components: {
    CategoryList,
    CustomButton,
    Confirm,
    Cancel,
    CategoryTitleEdit,
  },
  props: {
    id: { type: String, default: () => "", required: true },
    categories: { type: Array as PropType<Category[]>, default: () => [], required: true },
    isMobile: { type: Boolean, default: () => false, required: true },
    attachedCategories: { type: Array as PropType<Category[]>, default: () => [] },
    domainId: { type: String, default: () => "" },
    // ログインユーザー情報
    me: { type: Object as PropType<User>, default: () => User.createNotFoundUser() },
    // Storybook向け
    show: { type: Boolean, default: () => false },
  },
  setup(props, { emit }) {
    // 旧 TextProcessMixin / EditDiscardMixin → composable（1-D 利用側書き換え）
    const { getLength } = useTextProcess();
    const { changeEditMode, showEditDiscardAlert } = useEditDiscard();
    const rootEvent = useLegacyRootEvent();
    // $bvModal は BootstrapVue が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const categoriesStr = ref<string>("");
    const categorySource = ref<Category[]>([]);

    const selectedId = ref<string>("");

    const editCategory = ref<Category>(Category.createTemp(""));

    const clickedAddCategory = ref<boolean>(false);
    const alertCategoryStr = ref<string>("");

    // 条件を通過しなかったカテゴリー文字列(アラート用)
    const duplicatedInput = ref<string>("");
    const duplicated = ref<string>("");
    const overed = ref<string>("");

    const isEditing = ref<boolean>(false); // 編集中か

    const validTitleMessage = computed((): string => {
      return `カテゴリー名は${CATEGORY_TITLE_MAX_LENGTH}文字以内です`;
    });

    // 編集あったか
    const editMode = computed((): boolean => {
      const diff = categorySource.value.filter((category) => {
        const exists = props.categories.find((c) => c.id == category.id);
        if (exists) {
          // 既存カテゴリー更新/削除
          return (
            exists.title != category.title ||
            exists.color != category.color ||
            exists.deleted != category.deleted
          );
        } else {
          // 新規カテゴリー追加
          return true;
        }
      });

      return !!diff.length;
    });

    // 編集情報のリセット
    const resetParam = (): void => {
      categorySource.value = props.categories.slice();
      categoriesStr.value = "";
      isEditing.value = false;
    };

    // 選択カテゴリーの反映
    // イベントルート: CategoryListItem => CategoryList => CategoryEdit
    const selected = (param: { id: string }): void => {
      const category = categorySource.value.find((c) => c.id == param.id);
      if (category) {
        selectedId.value = param.id;
      }
    };

    /**
     * タイトル文字列から作成したカテゴリーをpushしたものを出力
     * 条件判定もここで行う
     */
    const pushCategories = (titleArray: string[]): Category[] => {
      const tempCategories = categorySource.value.slice();
      duplicatedInput.value = "";
      overed.value = "";
      duplicated.value = "";

      // 入力内の重複排除
      const noDuplicationArray = [...new Set(titleArray)];
      if (noDuplicationArray.length != titleArray.length) {
        const duplicatedInputArrray: string[] = []; // 重複したカテゴリー名の配列
        noDuplicationArray.map((str) => {
          const exist = titleArray.filter((title) => title === str);
          if (exist.length > 1) {
            duplicatedInputArrray.push(str);
          }
        });
        duplicatedInput.value = duplicatedInputArrray.join("\n");
      }

      // 入力されたカテゴリーが既に登録されていないか と 文字数制限のチェック
      const duplicatedArray: string[] = []; // 重複したカテゴリー名の配列
      const overedArray: string[] = []; // 文字数オーバーしたカテゴリー名の配列
      const results = noDuplicationArray.reduce((p: Category[], c: string): Category[] => {
        if (
          getLength(c) <= CATEGORY_TITLE_MAX_LENGTH &&
          !p.find((category) => category.title == c)
        ) {
          const tempPrev = p.slice();
          // 追加したカテゴリーも編集できるようにownerを付与しておく
          tempPrev.push(
            Category.createTemp({ title: c, domainId: props.domainId, owner: props.me.id })
          );
          return tempPrev;
        } else {
          if (getLength(c) > CATEGORY_TITLE_MAX_LENGTH) {
            overedArray.push(c);
          }
          if (p.find((category) => category.title == c)) {
            duplicatedArray.push(c);
          }
          return p;
        }
      }, tempCategories);
      duplicated.value = duplicatedArray.join("\n");
      overed.value = overedArray.join("\n");

      return results;
    };

    /**
     * 入力されたカテゴリー追加
     */
    const addCategories = (): void => {
      const categoriesArray = categoriesStr.value
        .split(/\n/)
        .map((str) => str.trim())
        .filter((str) => !!str);
      if (!categoriesArray.length) return;

      const pushedCategories = pushCategories(categoriesArray);

      // カテゴリーpush作業後のカテゴリー数 と push前のカテゴリー数+入力されたタイトル数が一致しているかどうか
      // 一致している = 入力された全カテゴリー名が条件をクリア(カテゴリー名被りなし,文字制限通過)
      // 一致していない = 入力されたカテゴリー名の中に条件をクリアしていないものが一つ以上ある
      if (pushedCategories.length == categorySource.value.length + categoriesArray.length) {
        clickedAddCategory.value = false;
        categorySource.value = pushedCategories;
        categoriesStr.value = "";
      } else {
        clickedAddCategory.value = true;
        alertCategoryStr.value = categoriesStr.value;
        vm?.$bvModal.show("category-add-alert");
      }
    };

    const showCancelAlert = (close: () => void): void => {
      changeEditMode(editMode.value);
      showEditDiscardAlert(close);
    };

    // 編集完了
    const onEditComplete = async (): Promise<void> => {
      // 編集中(DB反映)フラグ
      if (isEditing.value) {
        return;
      } else {
        isEditing.value = true;
      }

      // 追加処理
      categorySource.value
        .filter((category) => {
          const exists = props.categories.some((c) => c.id == category.id);
          return !exists;
        })
        .forEach((category) => {
          rootEvent.emit("on-category-create", { data: category });
        });

      // 更新処理
      categorySource.value
        .filter((category) => {
          if (category.deleted) return false; // 削除されたものはスキップ

          const before = props.categories.find((c) => c.id == category.id);
          if (!before) return false;
          else return before.title != category.title || before.color != category.color;
        })
        .forEach((category) => {
          rootEvent.emit("on-category-update", category);
        });

      // 削除処理
      categorySource.value
        .filter((category) => {
          const before = props.categories.find((c) => c.id == category.id);
          if (!before) return false;
          else return before.deleted != category.deleted;
        })
        .forEach((category) => {
          rootEvent.emit("on-category-delete", { domainId: props.domainId, id: category.id });
        });

      // ※ 削除されたカテゴリーが選択されていた場合は、「未選択」とする
      const selectedCategories = categorySource.value.filter(
        (category) => category.id == selectedId.value && !category.deleted
      );
      if (selectedCategories) {
        // カテゴリーの選択を反映
        // CategoryEdit => TopicEdit
        emit("onCloseCategoryFilterDialog", selectedCategories);
      }
      vm?.$bvModal.hide(props.id);
    };

    // created 相当（props の一回スナップショット。リアクティブ追跡は不要）
    onBeforeMount(() => {
      categorySource.value = props.categories.slice();
    });

    onMounted(() => {
      if (props.show) {
        vm?.$bvModal.show(props.id);
      }

      // カテゴリー名編集モーダル(CategoryTitleEdit)の展開
      // イベント発火箇所: CategoryListItemの編集ボタン
      rootEvent.on("edit-category-title", (param: { id: string }) => {
        const id = param.id;
        const category = categorySource.value.find((c) => c.id == id);
        if (category) {
          editCategory.value = category;
          vm?.$bvModal.show("category-title-edit");
        }
      });

      // カテゴリー名の編集を反映させる
      // イベント発火箇所: CategoryTitleEditの保存ボタン
      rootEvent.on("edited-category", (param: { id: string; title: string }) => {
        const id = param.id;
        const title = param.title;
        const index = categorySource.value.findIndex((c) => c.id == id);
        if (index > -1) {
          const category = categorySource.value[index];
          if (!category) return;
          if (Category.isTemporary(category) === false) {
            const updated = category.copyFrom({ title: title, color: category.color });
            categorySource.value.splice(index, 1, updated);
          } else {
            // 新規追加したカテゴリー(DB反映はまだ)の編集は再作成
            const updated = Category.createTemp({
              id: category.id,
              domainId: props.domainId,
              owner: props.me.id,
              title: title,
              color: category.color,
            });
            categorySource.value.splice(index, 1, updated);
          }
        }
      });

      // カテゴリーの削除を反映させる
      // イベント発火箇所: CategoryTitleEditの削除ボタン
      rootEvent.on("deleted-category", (param: { id: string }) => {
        const id = param.id;
        const index = categorySource.value.findIndex((c) => c.id == id);
        if (index > -1) {
          const category = categorySource.value[index];
          if (!category) return;
          if (Category.isTemporary(category) === false) {
            // 削除
            const deleted = category.copyFrom({ deleted: true, deletedUser: props.me.directId });
            categorySource.value.splice(index, 1, deleted);
          } else {
            // 新規追加したカテゴリー(DB反映はまだ)の削除はカテゴリーから取り除く
            categorySource.value.splice(index, 1);
          }
        }
      });

      // カテゴリーの色編集を反映させる
      // イベント発火箇所: CategoryListItemの色選択popupが閉じた時
      rootEvent.on("edit-category-color", (param: { id: string; color: string }) => {
        const id = param.id;
        const color = param.color;
        const index = categorySource.value.findIndex((c) => c.id == id);
        if (index > -1) {
          const category = categorySource.value[index];
          if (!category) return;
          if (Category.isTemporary(category) === false) {
            const updated = category.copyFrom({ title: category.title, color: color });
            categorySource.value.splice(index, 1, updated);
          } else {
            // 新規追加したカテゴリー(DB反映はまだ)の編集は再作成
            const updated = Category.createTemp({
              id: category.id,
              domainId: props.domainId,
              owner: props.me.id,
              title: category.title,
              color: color,
            });
            categorySource.value.splice(index, 1, updated);
          }
        }
      });

      // カテゴリーの編集をDBに保存
      // イベント発火箇所: App.vueの保存アラートでOKを押した時
      rootEvent.on("save-category-edit", async () => {
        await onEditComplete();
      });
    });

    return {
      categoriesStr,
      categorySource,
      selectedId,
      editCategory,
      clickedAddCategory,
      alertCategoryStr,
      duplicatedInput,
      duplicated,
      overed,
      isEditing,
      validTitleMessage,
      editMode,
      resetParam,
      selected,
      addCategories,
      pushCategories,
      showCancelAlert,
      onEditComplete,
    };
  },
});
</script>
